/**
 * Getting a chart into an exported document.
 *
 * A chart is drawn on a `<canvas>`, and a canvas has a property that quietly
 * ruins exports: **its bitmap is not part of its markup.** `outerHTML` gives
 * back `<canvas></canvas>` and nothing else, so a report exported to PDF would
 * print every chart as an empty rectangle.
 *
 * Silently, again. The page looks right on screen, the export succeeds, and
 * the blank spaces are found by whoever opens the file.
 *
 * So each chart registers its canvas here, and the export swaps it for a
 * picture of itself on the way out. The same trick the font embedding uses,
 * for the same reason: what leaves has to carry everything it needs.
 */

/** The canvases currently on the page, by the section that owns each. */
const canvases = new Map<string, HTMLCanvasElement>()

export function registerChartCanvas(id: string, canvas: HTMLCanvasElement): void {
  canvases.set(id, canvas)
}

export function unregisterChartCanvas(id: string): void {
  canvases.delete(id)
}

/**
 * A copy of the page with every chart turned into a picture.
 *
 * Works on a clone, so the page somebody is looking at is never touched: the
 * live canvases stay live, and an export does not blank the charts on screen
 * while it runs.
 *
 * A chart whose canvas cannot be read is left as it is rather than removed.
 * An empty frame in the right place is easier to explain than a document whose
 * layout silently shifted.
 */
export function withChartsAsPictures(page: Element): Element {
  const copy = page.cloneNode(true) as Element

  for (const placeholder of copy.querySelectorAll('canvas[data-chart-id]')) {
    const id = placeholder.getAttribute('data-chart-id')
    const live = id ? canvases.get(id) : undefined
    if (!live) continue

    let source: string
    try {
      source = live.toDataURL('image/png')
    } catch {
      // A canvas tainted by a cross-origin draw refuses to be read. Nothing
      // here draws from another origin, so this should not happen; leaving the
      // frame is still better than dropping it.
      continue
    }

    const picture = document.createElement('img')
    picture.src = source
    picture.alt = ''
    // The canvas is sized by CSS, and the picture has to fill the same box or
    // the chart arrives at its bitmap's own dimensions.
    picture.style.width = '100%'
    picture.style.height = '100%'
    picture.style.objectFit = 'contain'

    placeholder.replaceWith(picture)
  }

  return copy
}
